import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form, Input, Button, Space, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';

type ChangePasswordValues = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

type Props = {
  onSubmit?: (values: ChangePasswordValues) => Promise<void> | void;
  userId?: string;
};

const validationSchemaUsers = Yup.object({
  password: Yup.string()
    .required('Одоогийн нууц үгээ оруулна уу.')
    .min(4, 'Дор хаяж 4 тэмдэгт байна.'),
});

const validationSchemaMe = Yup.object({
  currentPassword: Yup.string()
    .required('Одоогийн нууц үгээ оруулна уу.')
    .min(4, 'Дор хаяж 4 тэмдэгт байна.'),
  password: Yup.string()
    .required('Шинэ нууц үгээ оруулна уу.')
    .required('Давтаж оруулна уу.')
    .oneOf([Yup.ref('password')], 'Давталт шинэ нууц үгтэй таарах ёстой.'),

  confirmPassword: Yup.string()
    .required('Давтаж оруулна уу.')
    .oneOf([Yup.ref('password')], 'Давталт шинэ нууц үгтэй таарах ёстой.'),
});

const ChangePasswordForm: React.FC<Props> = ({ onSubmit, userId }) => {
  const initialValues: ChangePasswordValues = {
    currentPassword: '',
    password: '',
    confirmPassword: '',
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <Formik
        initialValues={initialValues}
        validationSchema={userId ? validationSchemaUsers : validationSchemaMe}
        validateOnBlur
        validateOnChange
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            if (onSubmit) {
              await onSubmit(values);
            } else {
              await new Promise((r) => setTimeout(r, 600));
            }
            message.success('Нууц үг амжилттай шинэчлэгдлээ.');
            resetForm();
          } catch (e: any) {
            message.error(e?.message || 'Шинэчлэх үед алдаа гарлаа.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form noValidate onSubmit={handleSubmit} style={{ marginTop: 16 }}>
            <Form layout="vertical">
              {userId ? (
                <>
                  <Form.Item
                    label="Шинэ нууц үг"
                    required
                    validateStatus={touched.password && errors.password ? 'error' : ''}
                    help={touched.password && errors.password ? errors.password : null}
                  >
                    <Input.Password
                      name="password"
                      size="large"
                      autoComplete="new-password"
                      prefix={<LockOutlined />}
                      value={values.password}
                      onChange={(e) => setFieldValue('password', e.target.value)}
                      onBlur={handleBlur}
                      placeholder="Шинэ нууц үг"
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    label="Одоогийн нууц үг"
                    required
                    validateStatus={
                      touched.currentPassword && errors.currentPassword ? 'error' : ''
                    }
                    help={
                      touched.currentPassword && errors.currentPassword
                        ? errors.currentPassword
                        : null
                    }
                  >
                    <Input.Password
                      name="currentPassword"
                      size="large"
                      autoComplete="current-password"
                      prefix={<LockOutlined />}
                      value={values.currentPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Одоогийн нууц үг"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Шинэ нууц үг"
                    required
                    validateStatus={touched.password && errors.password ? 'error' : ''}
                    help={touched.password && errors.password ? errors.password : null}
                  >
                    <Input.Password
                      name="password"
                      size="large"
                      autoComplete="new-password"
                      prefix={<LockOutlined />}
                      value={values.password}
                      onChange={(e) => setFieldValue('password', e.target.value)}
                      onBlur={handleBlur}
                      placeholder="Шинэ нууц үг"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Шинэ нууц үг давтан оруулна уу"
                    required
                    validateStatus={
                      touched.confirmPassword && errors.confirmPassword ? 'error' : ''
                    }
                    help={
                      touched.confirmPassword && errors.confirmPassword
                        ? errors.confirmPassword
                        : null
                    }
                  >
                    <Input.Password
                      name="confirmPassword"
                      size="large"
                      autoComplete="new-password"
                      prefix={<LockOutlined />}
                      value={values.confirmPassword}
                      onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                      onBlur={handleBlur}
                      placeholder="Шинэ нууц үг давтан оруулна уу"
                    />
                  </Form.Item>
                </>
              )}

              <Space style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  onClick={() => handleSubmit()}
                  size="large"
                  loading={isSubmitting}
                >
                  Нууц үг солих
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    (document.activeElement as HTMLElement)?.blur?.();
                    // formik reset
                    setFieldValue('currentPassword', '');
                    setFieldValue('password', '');
                    setFieldValue('confirmPassword', '');
                  }}
                >
                  Цэвэрлэх
                </Button>
              </Space>
            </Form>
          </form>
        )}
      </Formik>
    </div>
  );
};

export { ChangePasswordForm };
